import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import {
  getDuplicateIncidents,
  type DuplicateIncidentsFilter,
  type DuplicateIncidentRow,
} from "@/lib/duplicate-incidents.functions";
import { AlertTriangle, Search, Copy, Layers } from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/duplicate-incidents")({
  component: DuplicateIncidentsPage,
  head: () => ({ meta: [{ title: "Duplicate Incidents — InboxPulse" }] }),
});

const EVENT_TYPES = ["", "open", "click", "bounce", "complaint", "block", "delayed", "sent"];
const TIME_WINDOWS = [
  { hours: 1, label: "Last 1h" },
  { hours: 6, label: "Last 6h" },
  { hours: 24, label: "Last 24h" },
  { hours: 24 * 7, label: "Last 7d" },
  { hours: 24 * 30, label: "Last 30d" },
];

function DuplicateIncidentsPage() {
  const run = useServerFn(getDuplicateIncidents);
  const [filter, setFilter] = useState<DuplicateIncidentsFilter>({
    fromHours: 24,
    campaignId: "",
    idempotencyKey: "",
    eventType: "",
    limit: 200,
  });

  const mutation = useMutation({
    mutationFn: (f: DuplicateIncidentsFilter) =>
      run({
        data: {
          fromHours: f.fromHours,
          campaignId: f.campaignId || null,
          idempotencyKey: f.idempotencyKey || null,
          eventType: f.eventType || null,
          limit: f.limit,
        },
      }),
  });

  // Initial load.
  useEffect(() => {
    mutation.mutate(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const data = mutation.data;
  const err = mutation.error;
  const isAdminError = err instanceof Error && /forbidden|admin/i.test(err.message);

  // Group incidents by campaign for the per-campaign report view.
  const byCampaign = new Map<string, { name: string | null; rows: DuplicateIncidentRow[] }>();
  for (const i of data?.incidents ?? []) {
    let entry = byCampaign.get(i.campaign_id);
    if (!entry) {
      entry = { name: i.campaign_name, rows: [] };
      byCampaign.set(i.campaign_id, entry);
    }
    entry.rows.push(i);
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <PageHeader
        title="Duplicate Incidents"
        description="Every drain batch that contained two or more queue rows sharing the same idempotency_key. Grouped per campaign within the selected time window."
      />

      <HelpPanel
        title="Hilfe: Doppelte Events finden"
        what={
          <>
            Hier siehst du Fälle, wo das System einen <b>Pixel-Hit doppelt</b> bekommen hat. Wird
            automatisch ignoriert, aber wir loggen es zur Sicherheit.
          </>
        }
        why={
          <>
            Viele Duplikate können bedeuten: Empfänger-Mail-Anbieter lädt das Bild mehrfach (z.B.
            Apple Vorlade-Cache) — oder ein Problem im Versand.
          </>
        }
        how={[
          <>Zeitfenster wählen.</>,
          <>Liste nach Kampagne gruppiert — viele Duplikate → der Kampagne genauer ansehen.</>,
        ]}
        tips={[<>Apple Mail Privacy bringt fast immer ein paar Duplikate — das ist normal.</>]}
        glossary={[
          {
            term: "Idempotency-Key",
            def: "Eindeutiger Schlüssel pro Treffer. Gleicher Key zweimal = Duplikat.",
          },
        ]}
      />

      <Card className="p-4 mb-6">
        <form
          className="grid grid-cols-1 md:grid-cols-5 gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate(filter);
          }}
        >
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Time window</label>
            <select
              value={filter.fromHours}
              onChange={(e) => setFilter({ ...filter, fromHours: Number(e.target.value) })}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {TIME_WINDOWS.map((w) => (
                <option key={w.hours} value={w.hours}>
                  {w.label}
                </option>
              ))}
            </select>
          </div>
          <Field
            label="Campaign ID"
            value={filter.campaignId ?? ""}
            onChange={(v) => setFilter({ ...filter, campaignId: v })}
            placeholder="uuid"
            mono
          />
          <Field
            label="Idempotency key"
            value={filter.idempotencyKey ?? ""}
            onChange={(v) => setFilter({ ...filter, idempotencyKey: v })}
            placeholder="open:campaign:bucket"
            mono
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Event type</label>
            <select
              value={filter.eventType ?? ""}
              onChange={(e) => setFilter({ ...filter, eventType: e.target.value })}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {EVENT_TYPES.map((t) => (
                <option key={t || "any"} value={t}>
                  {t || "Any"}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={mutation.isPending}
              className="h-9 w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50 transition"
            >
              <Search className="size-4" />
              {mutation.isPending ? "Loading…" : "Search"}
            </button>
          </div>
        </form>
      </Card>

      {err && (
        <Card className="p-4 mb-6 border-destructive/50 bg-destructive/5 text-destructive flex items-start gap-3">
          <AlertTriangle className="size-5 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">
              {isAdminError ? "Admin access required" : "Query failed"}
            </div>
            <div className="text-sm opacity-80 mt-1">
              {err instanceof Error ? err.message : String(err)}
            </div>
          </div>
        </Card>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Stat label="Incidents" value={data.summary.totalIncidents} />
            <Stat label="Duplicate rows" value={data.summary.totalDuplicateRows} />
            <Stat label="Affected campaigns" value={data.summary.affectedCampaigns} />
            <Stat label="Distinct keys" value={data.summary.affectedKeys} />
          </div>

          {byCampaign.size === 0 ? (
            <Card className="p-8 text-center text-sm text-muted-foreground">
              No duplicate idempotency_key incidents in this window.
            </Card>
          ) : (
            Array.from(byCampaign.entries()).map(([campaignId, group]) => (
              <Card key={campaignId} className="p-0 mb-6 overflow-hidden">
                <div className="px-4 py-3 border-b border-border/50 flex items-center gap-3 bg-muted/30">
                  <Layers className="size-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {group.name ?? "Unknown campaign"}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono truncate">
                      {campaignId}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground tabular-nums">
                    {group.rows.length} incident{group.rows.length === 1 ? "" : "s"}
                  </div>
                </div>
                <IncidentsTable rows={group.rows} />
              </Card>
            ))
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <Card className="p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold tabular-nums mt-1">{value}</div>
    </Card>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  mono,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-muted-foreground">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(
          "h-9 rounded-md border border-input bg-background px-2 text-sm",
          mono && "font-mono text-xs",
        )}
      />
    </div>
  );
}

function CopyableId({ value }: { value: string | null }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return (
    <button
      onClick={() => navigator.clipboard.writeText(value)}
      title={value}
      className="font-mono text-xs hover:underline inline-flex items-center gap-1 max-w-[220px] truncate"
    >
      <span className="truncate">{value}</span>
      <Copy className="size-3 opacity-50 shrink-0" />
    </button>
  );
}

function IncidentsTable({ rows }: { rows: DuplicateIncidentRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-muted-foreground text-xs">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Observed</th>
            <th className="text-left px-3 py-2 font-medium">Event</th>
            <th className="text-left px-3 py-2 font-medium">Idempotency key</th>
            <th className="text-right px-3 py-2 font-medium">Dupes</th>
            <th className="text-left px-3 py-2 font-medium">Surviving event</th>
            <th className="text-left px-3 py-2 font-medium">Queue rows</th>
            <th className="text-left px-3 py-2 font-medium">Request IDs</th>
            <th className="text-left px-3 py-2 font-medium">Enqueued timestamps</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-border/50 align-top">
              <td className="px-3 py-2 text-xs whitespace-nowrap">
                {new Date(r.observed_at).toLocaleString()}
              </td>
              <td className="px-3 py-2 text-xs">{r.event_type}</td>
              <td className="px-3 py-2">
                <CopyableId value={r.idempotency_key} />
              </td>
              <td className="px-3 py-2 text-xs tabular-nums text-right font-medium">
                {r.duplicate_count}
              </td>
              <td className="px-3 py-2">
                <CopyableId value={r.surviving_event_id} />
              </td>
              <td className="px-3 py-2">
                <div className="flex flex-col gap-0.5">
                  {r.queue_ids.map((id) => (
                    <CopyableId key={id} value={id} />
                  ))}
                </div>
              </td>
              <td className="px-3 py-2">
                <div className="flex flex-col gap-0.5">
                  {r.request_ids.length === 0 ? (
                    <span className="text-muted-foreground text-xs">—</span>
                  ) : (
                    r.request_ids.map((id) => <CopyableId key={id} value={id} />)
                  )}
                </div>
              </td>
              <td className="px-3 py-2 text-xs">
                <div className="flex flex-col gap-0.5">
                  {r.enqueued_ats.map((ts, i) => (
                    <span key={i} className="whitespace-nowrap text-muted-foreground">
                      {new Date(ts).toLocaleString()}
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
